import { ouvrir, lireTout } from './base.js';
import { lireReglages, ecrireReglages } from './reglages.js';
import { MAGASINS, deciderFusion, aPousser, typer, detyper, resumer, joindreUrl } from '../sync.js';

/**
 * Transport de la synchronisation - document 02, section 7.
 *
 * Ce module ne décide de rien : il lit, appelle le serveur, applique ce que
 * `sync.js` a décidé, et écrit. Toute règle d'arbitrage qui apparaîtrait ici
 * serait à déplacer.
 *
 * Le serveur est un miroir (AD-1) : son indisponibilité n'est pas une erreur de
 * l'application, seulement une synchro remise à plus tard.
 */

/** Erreurs distinguées parce qu'elles appellent des réponses différentes. */
export const RAISONS = {
  NON_CONFIGURE: 'non-configure',
  HORS_LIGNE: 'hors-ligne',
  AUTH: 'auth',
  SERVEUR: 'serveur',
};

class EchecSync extends Error {
  constructor(raison, message) { super(message); this.raison = raison; }
}

async function appeler(url, jeton, chemin, options = {}) {
  let reponse;
  try {
    reponse = await fetch(joindreUrl(url, chemin), {
      ...options,
      headers: { Authorization: `Bearer ${jeton}`, ...(options.headers ?? {}) },
    });
  } catch {
    // Réseau absent ou serveur injoignable. Ce n'est pas une panne de
    // l'application : elle reste pleinement utilisable hors-ligne.
    throw new EchecSync(RAISONS.HORS_LIGNE, 'Serveur injoignable');
  }

  if (reponse.status === 401) {
    throw new EchecSync(RAISONS.AUTH, "Jeton d'appareil refusé");
  }
  if (reponse.status === 503) {
    // Le serveur tourne mais n'a pas de jeton configuré : il refuse de servir
    // la synchro ouverte. Distinct d'un jeton erroné côté client.
    throw new EchecSync(RAISONS.SERVEUR, 'Synchronisation désactivée côté serveur');
  }
  if (!reponse.ok) {
    throw new EchecSync(RAISONS.SERVEUR, `Le serveur a répondu ${reponse.status}`);
  }
  return reponse.json();
}

/** Toutes les entités locales synchronisables, supprimées comprises. */
async function toutesLocales() {
  const db = await ouvrir();
  const paires = [];
  for (const [type, magasin] of Object.entries(MAGASINS)) {
    // getAll et non lireTout : les entités supprimées doivent être poussées
    // pour que la suppression se propage.
    for (const e of await db.getAll(magasin)) paires.push(typer(e, type));
  }
  return paires;
}

/**
 * Un tour de synchronisation : pull, fusion, push.
 *
 * @returns {{resume: string, lastSyncAt: string}}
 */
export async function synchroniser() {
  const reglages = await lireReglages();
  if (!reglages.syncUrl || !reglages.deviceToken) {
    throw new EchecSync(RAISONS.NON_CONFIGURE, 'URL ou jeton non renseignés');
  }

  const { syncUrl, deviceToken, lastSyncAt } = reglages;

  // --- PULL
  const depuis = lastSyncAt ? `?since=${encodeURIComponent(lastSyncAt)}` : '';
  const { entities: distantes = [], serverTime } = await appeler(
    syncUrl, deviceToken, `/sync/changes${depuis}`
  );

  const locales = new Map((await toutesLocales()).map((e) => [`${e.type}:${e.id}`, e]));
  const { aEcrire } = deciderFusion(distantes, locales);

  const db = await ouvrir();
  for (const e of aEcrire) await db.put(MAGASINS[e.type], detyper(e));

  // --- PUSH
  // Recalculé après écriture : une entité qu'on vient de recevoir n'a aucune
  // raison de repartir vers le serveur.
  const apresFusion = await toutesLocales();
  const envoi = aPousser(apresFusion, lastSyncAt);

  let refuses = 0;
  if (envoi.length) {
    const { rejected = [] } = await appeler(syncUrl, deviceToken, '/sync/push', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ entities: envoi }),
    });
    refuses = rejected.length;
  }

  // L'heure du serveur fait foi : deux horloges qui dérivent feraient manquer
  // des entités au prochain pull.
  const nouveau = serverTime ?? new Date().toISOString();
  await ecrireReglages({ lastSyncAt: nouveau });

  return {
    resume: resumer({ recus: distantes.length, ecrits: aEcrire.length, pousses: envoi.length, refuses }),
    lastSyncAt: nouveau,
  };
}

export { EchecSync };
